import { useState } from "react";
import { styles } from "../styles";
import { createLocation } from "../models/models";
import { hasCapability } from "../utils/capabilities";
import ContactsEditor from "../components/ContactsEditor";
import BackButton from "../components/BackButton";

export default function CustomerDetailPage({
  customer,
  contactsRaw = [],
  actions,
  role,
  capabilities,
  user,
  setSelectedCustomer,
  setSelectedLocation,
  setSelectedContactLocation,
  setSelectedContactCustomer,
}) {
  // Contacts stay role-gated (admin/manager). Add/Edit Location are
  // capability-gated so granted technicians (and non-restricted
  // managers) can do them.
  const canEdit = role === "admin" || role === "manager";
  const canAddLocations = hasCapability(role, capabilities, "addLocations");
  const canEditLocations = hasCapability(role, capabilities, "editLocations");
  const canShareCustomer = hasCapability(role, capabilities, "editCustomers");

  const genCode = () => Math.random().toString(36).slice(2, 10).toUpperCase();

  const [showAddLocation, setShowAddLocation] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [locationStreet, setLocationStreet] = useState("");
  const [locationCity, setLocationCity] = useState("");
  const [locationState, setLocationState] = useState("");
  const [locationZip, setLocationZip] = useState("");
  const [locationBuilding, setLocationBuilding] = useState("");
  const [locationAccessNotes, setLocationAccessNotes] = useState("");
  const [locationPoc1Name, setLocationPoc1Name] = useState("");
  const [locationPoc1Phone, setLocationPoc1Phone] = useState("");
  const [locationPoc2Name, setLocationPoc2Name] = useState("");
  const [locationPoc2Phone, setLocationPoc2Phone] = useState("");

  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editLocName, setEditLocName] = useState("");
  const [editLocStreet, setEditLocStreet] = useState("");
  const [editLocCity, setEditLocCity] = useState("");
  const [editLocState, setEditLocState] = useState("");
  const [editLocZip, setEditLocZip] = useState("");
  const [editLocBuilding, setEditLocBuilding] = useState("");
  const [editLocAccessNotes, setEditLocAccessNotes] = useState("");
  const [editLocPoc1Name, setEditLocPoc1Name] = useState("");
  const [editLocPoc1Phone, setEditLocPoc1Phone] = useState("");
  const [editLocPoc2Name, setEditLocPoc2Name] = useState("");
  const [editLocPoc2Phone, setEditLocPoc2Phone] = useState("");

  const [openLocationDetails, setOpenLocationDetails] = useState({});
  const [showShareCustomer, setShowShareCustomer] = useState(false);


  const addLocation = async () => {
    if (!locationName) return;

    const newLocation = {
      ...createLocation(locationName),
      street: locationStreet,
      city: locationCity,
      state: locationState,
      zip: locationZip,
      building: locationBuilding,
      accessNotes: locationAccessNotes,
      poc1Name: locationPoc1Name,
      poc1Phone: locationPoc1Phone,
      poc2Name: locationPoc2Name,
      poc2Phone: locationPoc2Phone,
    };

    await actions.addLocation(customer.id, newLocation);
    setLocationName("");
    setLocationStreet("");
    setLocationCity("");
    setLocationState("");
    setLocationZip("");
    setLocationBuilding("");
    setLocationAccessNotes("");
    setLocationPoc1Name("");
    setLocationPoc1Phone("");
    setLocationPoc2Name("");
    setLocationPoc2Phone("");
  };

  const startEditingLocation = (location) => {
    setEditingLocationId(location.id);
    setEditLocName(location.name || "");
    setEditLocStreet(location.street || "");
    setEditLocCity(location.city || "");
    setEditLocState(location.state || "");
    setEditLocZip(location.zip || "");
    setEditLocBuilding(location.building || "");
    setEditLocAccessNotes(location.accessNotes || "");
    setEditLocPoc1Name(location.poc1Name || "");
    setEditLocPoc1Phone(location.poc1Phone || "");
    setEditLocPoc2Name(location.poc2Name || "");
    setEditLocPoc2Phone(location.poc2Phone || "");
  };

  const cancelEditingLocation = () => {
    setEditingLocationId(null);
  };

  // Mirror CustomersPage's collapse-while-editing behavior: clicking the
  // triangle on a row that's currently being edited auto-cancels the edit
  // along with the collapse, so the user doesn't have to hit Cancel first.
  const toggleLocationDetails = (location) => {
    const closing = !!openLocationDetails[location.id];
    if (closing && editingLocationId === location.id) {
      cancelEditingLocation();
    }
    setOpenLocationDetails((prev) => ({
      ...prev,
      [location.id]: !prev[location.id],
    }));
  };

  const updateLocation = async () => {
    if (!editLocName || !editingLocationId) return;

    await actions.updateLocation(editingLocationId, {
      name: editLocName,
      street: editLocStreet,
      city: editLocCity,
      state: editLocState,
      zip: editLocZip,
      building: editLocBuilding,
      accessNotes: editLocAccessNotes,
      poc1Name: editLocPoc1Name,
      poc1Phone: editLocPoc1Phone,
      poc2Name: editLocPoc2Name,
      poc2Phone: editLocPoc2Phone,
    });
    setEditingLocationId(null);
  };

  return (
    <>
      <BackButton onClick={() => setSelectedCustomer(null)} />

      <h2>{customer?.name}</h2>

      {canAddLocations && (
        <button
          style={styles.button}
          onClick={() => setShowAddLocation(!showAddLocation)}
        >
          {showAddLocation ? "Hide Add Location" : "Add Location"}
        </button>
      )}

      {canAddLocations && showAddLocation && (
        <form
          style={styles.card}
          onSubmit={(e) => {
            e.preventDefault();
            addLocation();
          }}
        >
          <input
            style={styles.input}
            placeholder="Location Name"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="Street Address"
            value={locationStreet}
            onChange={(e) => setLocationStreet(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="City"
            value={locationCity}
            onChange={(e) => setLocationCity(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="State"
            value={locationState}
            onChange={(e) => setLocationState(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="ZIP Code"
            value={locationZip}
            onChange={(e) => setLocationZip(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="Building / Suite / Unit"
            value={locationBuilding}
            onChange={(e) => setLocationBuilding(e.target.value)}
          />
          <textarea
            style={styles.textarea}
            placeholder="Gate Codes / Access Notes / Parking Instructions"
            value={locationAccessNotes}
            onChange={(e) => setLocationAccessNotes(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="POC 1 Name"
            value={locationPoc1Name}
            onChange={(e) => setLocationPoc1Name(e.target.value)}
          />
          <input
            style={styles.input}
            type="tel"
            placeholder="POC 1 Phone"
            value={locationPoc1Phone}
            onChange={(e) => setLocationPoc1Phone(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="POC 2 Name"
            value={locationPoc2Name}
            onChange={(e) => setLocationPoc2Name(e.target.value)}
          />
          <input
            style={styles.input}
            type="tel"
            placeholder="POC 2 Phone"
            value={locationPoc2Phone}
            onChange={(e) => setLocationPoc2Phone(e.target.value)}
          />
          <button type="submit" style={styles.button}>
            Add Location
          </button>
        </form>
      )}

      <h3>Locations</h3>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {[...(customer?.locations || [])]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((location) => (
            <li
              key={location.id}
              style={{ ...styles.card, maxWidth: "780px" }}
            >
              <div
                onClick={() => toggleLocationDetails(location)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: "monospace",
                    color: "#666",
                    width: "12px",
                  }}
                >
                  {openLocationDetails[location.id] ? "▼" : "▶"}
                </span>
                <strong>{location.name}</strong>
                <span style={{ fontSize: "12px", color: "#666" }}>
                  Equipment: {location.equipment.length}
                </span>
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    gap: "6px",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    style={{ ...styles.button, margin: 0 }}
                    onClick={() => setSelectedLocation(location)}
                  >
                    Open
                  </button>
                </div>
              </div>

              {openLocationDetails[location.id] && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginTop: "6px",
                  }}
                >
                  {editingLocationId === location.id ? (
                    <div
                      style={{
                        ...styles.card,
                        background: "#fdfdfd",
                        border: "1px solid #eee",
                        marginTop: "6px",
                        color: "#222",
                        fontSize: "13px",
                      }}
                    >
                      <h3 style={{ marginTop: 0 }}>Edit Location</h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: "10px",
                        }}
                      >
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            Location Name
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            placeholder="Required"
                            value={editLocName}
                            onChange={(e) => setEditLocName(e.target.value)}
                          />
                        </div>
                      </div>

                      <h4 style={{ margin: "16px 0 6px" }}>Address</h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: "10px",
                        }}
                      >
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            Street
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            value={editLocStreet}
                            onChange={(e) =>
                              setEditLocStreet(e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            Suite
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            value={editLocBuilding}
                            onChange={(e) =>
                              setEditLocBuilding(e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            City
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            value={editLocCity}
                            onChange={(e) =>
                              setEditLocCity(e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            State
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            value={editLocState}
                            onChange={(e) =>
                              setEditLocState(e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            Zip
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            value={editLocZip}
                            onChange={(e) =>
                              setEditLocZip(e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <h4 style={{ margin: "16px 0 6px" }}>
                        Points of Contact
                      </h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: "10px",
                        }}
                      >
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            POC 1 Name
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            value={editLocPoc1Name}
                            onChange={(e) =>
                              setEditLocPoc1Name(e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            POC 1 Phone
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            type="tel"
                            value={editLocPoc1Phone}
                            onChange={(e) =>
                              setEditLocPoc1Phone(e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: "10px",
                          marginTop: "10px",
                        }}
                      >
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            POC 2 Name
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            value={editLocPoc2Name}
                            onChange={(e) =>
                              setEditLocPoc2Name(e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            POC 2 Phone
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              margin: 0,
                              width: "100%",
                            }}
                            type="tel"
                            value={editLocPoc2Phone}
                            onChange={(e) =>
                              setEditLocPoc2Phone(e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <h4 style={{ margin: "16px 0 6px" }}>Access Notes</h4>
                      <textarea
                        style={{ ...styles.textarea, maxWidth: "100%" }}
                        rows={3}
                        value={editLocAccessNotes}
                        onChange={(e) =>
                          setEditLocAccessNotes(e.target.value)
                        }
                      />

                      {canEdit && (
                        <ContactsEditor
                          customerId={customer.id}
                          locationId={location.id}
                          contacts={contactsRaw.filter(
                            (c) =>
                              c.customerId === customer.id &&
                              c.locationId === location.id &&
                              !c.deleted
                          )}
                          actions={actions}
                          currentUserEmail={user?.email}
                        />
                      )}

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "12px",
                        }}
                      >
                        <button
                          style={{ ...styles.primaryButton, margin: 0 }}
                          onClick={updateLocation}
                          disabled={!editLocName}
                        >
                          Save
                        </button>
                        <button
                          style={{ ...styles.button, margin: 0 }}
                          onClick={cancelEditingLocation}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <strong>Address:</strong> {location.street || "-"}
                      <br />
                      <strong>City/State/ZIP:</strong>{" "}
                      {location.city || "-"}, {location.state || "-"}{" "}
                      {location.zip || "-"}
                      <br />
                      <strong>Building/Suite:</strong>{" "}
                      {location.building || "-"}
                      <br />
                      <strong>Access Notes:</strong>{" "}
                      {location.accessNotes || "-"}
                      <br />
                      <strong>POC 1:</strong> {location.poc1Name || "-"}
                      {location.poc1Phone ? ` — ${location.poc1Phone}` : ""}
                      <br />
                      <strong>POC 2:</strong> {location.poc2Name || "-"}
                      {location.poc2Phone ? ` — ${location.poc2Phone}` : ""}

                      {/* Read-only contacts list grouped by department,
                          mirrors CustomerInfoCard's display. ContactsEditor
                          with the +Add Contact button only appears in
                          edit mode. */}
                      <h4 style={{ margin: "12px 0 6px", color: "#222" }}>
                        Contacts by Department
                      </h4>
                      {(() => {
                        const locationContacts = contactsRaw.filter(
                          (c) =>
                            c.customerId === customer.id &&
                            c.locationId === location.id &&
                            !c.deleted
                        );
                        if (locationContacts.length === 0) {
                          return (
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#666",
                                fontStyle: "italic",
                              }}
                            >
                              No contacts added yet. Click Edit to add some.
                            </div>
                          );
                        }
                        const byDept = new Map();
                        for (const c of locationContacts) {
                          const key =
                            (c.department || "Other").trim() || "Other";
                          if (!byDept.has(key)) byDept.set(key, []);
                          byDept.get(key).push(c);
                        }
                        const deptOrder = [...byDept.keys()].sort();
                        return deptOrder.map((dept) => (
                          <div key={dept} style={{ marginBottom: "6px" }}>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#666",
                                fontWeight: 600,
                              }}
                            >
                              {dept}
                            </div>
                            {byDept.get(dept).map((c) => (
                              <div
                                key={c.id}
                                style={{ fontSize: "13px", color: "#222" }}
                              >
                                {c.name || "(unnamed)"}
                                {c.phone ? ` · ${c.phone}` : ""}
                                {c.email ? ` · ${c.email}` : ""}
                              </div>
                            ))}
                          </div>
                        ));
                      })()}

                      {canEditLocations && (
                        <div style={{ marginTop: "10px" }}>
                          <button
                            style={{ ...styles.button, margin: 0 }}
                            onClick={() => startEditingLocation(location)}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
      </ul>

      {canShareCustomer && (
        <div style={{ marginTop: "12px" }}>
          <button
            style={styles.button}
            onClick={() => setShowShareCustomer((s) => !s)}
          >
            {showShareCustomer ? "▼ Share Customer" : "▶ Share Customer"}
          </button>
          {showShareCustomer && (
            <div style={{ ...styles.card, marginTop: "6px" }}>
              <p style={{ fontSize: "12px", color: "#666", margin: "0 0 6px" }}>
                One code that links the other org to this customer and{" "}
                <em>every</em> building under it — for a whole-facility contract.
              </p>
              {customer?.inviteCode ? (
                <div style={{ fontSize: "13px" }}>
                  Share code: <strong>{customer.inviteCode}</strong>{" "}
                  <button
                    style={styles.button}
                    onClick={() =>
                      actions.updateCustomer(customer.id, {
                        inviteCode: genCode(),
                      })
                    }
                  >
                    Regenerate
                  </button>
                </div>
              ) : (
                <button
                  style={styles.button}
                  onClick={() =>
                    actions.updateCustomer(customer.id, {
                      inviteCode: genCode(),
                    })
                  }
                >
                  Generate share code
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
